import React from "react";
import dayjs, { Dayjs } from 'dayjs'
import { Mark, DefectReason } from '../../../../../../../enums'
import type { FormikHelpers } from 'formik'

type RawDlc =
  | string
  | Date
  | Dayjs
  | number[]

type Options = {
  setFieldValue: FormikHelpers<any>['setFieldValue']
  onScan: (code: string) => void
  threshold?: number
  afterChange?: (idx: number, key: string, raw: string) => void
  getValue?: (index: number, key: 'integral' | 'broken') =>
    | string
    | number
    | null
    | undefined
}

export const getInitialValues = (products, offlineData) => {
  const newProductsList = products?.productList?.map((product, index) => {
    const apiDlc = Array.isArray(product.dlc)
      ? dayjs(new Date(product.dlc[0], product.dlc[1] - 1, product.dlc[2]))
      : product.dlc ? dayjs(product.dlc) : null

    const offlineDlc = offlineData?.[`offlineProductList[${index}].dlc`]
    const initialDlc = offlineDlc && offlineDlc !== "Invalid Date"
      ? dayjs(offlineDlc)
      : apiDlc

    const markVal = offlineData?.[`offlineProductList[${index}].mark`] ?? product.mark
    const defectVal = offlineData?.[`offlineProductList[${index}].defectReason`] ?? product.defectReason
    const integral = offlineData?.[`offlineProductList[${index}].integral`] ?? product.integral ?? 0
    const broken   = offlineData?.[`offlineProductList[${index}].broken`]   ?? product.broken   ?? 0
    const qty       = product.quantity ?? 0
    const dev       = (product.shopReceptionDeviation ?? 0) / 1000
    const total     = integral + broken

    let litigation: 0 | 1 | 2
    let marks: Mark[] = []

    if (qty > integral + dev && total === qty) {
      litigation = 2
      marks = [
        Mark.VEGETABLE_FRUIT_QUALITY,
        Mark.EXPIRED,
        Mark.SHORT_TERM,
        Mark.DAMAGED,
        Mark.VEGETABLES_FRUITS,
        Mark.DETERIORATION_A
      ]
    } else if (qty > integral + dev && total !== qty) {
      litigation = 1
      marks = [
        Mark.PALLETS_MERCHANDISE_MISSING,
        Mark.MISSING_CARRIER,
        Mark.QUANTITATIVE_DIFFERENCE
      ]
    } else if (qty < integral - dev) {
      litigation = 1
      marks = [
        Mark.PALLETS_MERCHANDISE_MISSING,
        Mark.QUANTITATIVE_DIFFERENCE
      ]
    } else {
      litigation = 0
    }

    let defectReasons: DefectReason[] = []
    if (litigation === 1) {
      defectReasons = [DefectReason.PROVIDER]
    } else if (litigation === 2) {
      if (markVal === Mark.EXPIRED || markVal === Mark.SHORT_TERM) {
        defectReasons = [DefectReason.PROVIDER]
      } else if (markVal === Mark.VEGETABLE_FRUIT_QUALITY) {
        defectReasons = [DefectReason.PROVIDER, DefectReason.SHOP]
      } else if (markVal === Mark.DAMAGED) {
        defectReasons = [
          DefectReason.PROVIDER,
          DefectReason.TRANSPORTER,
          DefectReason.SHOP
        ]
      } else if (
        markVal === Mark.VEGETABLES_FRUITS ||
        markVal === Mark.DETERIORATION_A
      ) {
        defectReasons = [
          DefectReason.PROVIDER,
          DefectReason.TRANSPORTER
        ]
      }
    }

    const defLabel = (DefectReason as Record<string,string>)[defectVal] || defectVal;
    const markLabel = (Mark as Record<string,string>)[markVal] ?? markVal

    return {
      ...product,
      broken: offlineData?.[`offlineProductList[${index}].broken`] ?? product.broken,
      integral: offlineData?.[`offlineProductList[${index}].integral`] ?? product.integral,
      mark: markLabel,
      defectReason: defLabel,
      marks,
      defectReasons,

      dlc: initialDlc,
      productDlc: initialDlc,
      showDlc: product.dlc !== null || product.shortDlc === true
    }
  })

  const safeSupport = products && Array.isArray(products.productList)
    ? products
    : { productList: [] };

  return { ...safeSupport, productList: newProductsList ?? [] }
}

export function normalizeDlc(raw?: RawDlc | null): Dayjs | null {
  if (raw == null) return null

  let d: Dayjs
  if (Array.isArray(raw)) {
    const [Y, M, D] = raw
    d = dayjs(new Date(Y, M - 1, D))
  } else {
    d = dayjs(raw)
  }

  return d.isValid() ? d.startOf('day') : null
}

export function dlcIsSame(a?: RawDlc | null, b?: RawDlc | null): boolean {
  const da = normalizeDlc(a)
  const db = normalizeDlc(b)
  if (!da || !db) return false
  return da.isSame(db, 'day')
}

export function makeArrayScanAwareDecimalChange({
  setFieldValue,
  onScan,
  threshold = 5,
  afterChange,
  getValue
}: Options) {
  return (index: number, key: 'integral' | 'broken') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let v = e.target.value

      const dot = v.indexOf('.')
      const comma = v.indexOf(',')
      const sepIdx = dot !== -1 && comma !== -1 ? Math.min(dot, comma) : (dot !== -1 ? dot : comma)
      const intPart = sepIdx === -1 ? v : v.slice(0, sepIdx)
      const intDigits = intPart.replace(/\D/g, '')

      if (intDigits.length > threshold) {
        let scanDigits = v.replace(/\D/g, '')

        const prev = getValue ? String(getValue(index, key) ?? '') : '';
        if (prev === '0' && v.startsWith('0') && scanDigits.length > 0) {
          scanDigits = scanDigits.slice(1)
        }

        setFieldValue(`productList[${index}].${key}`, 0)
        onScan(scanDigits)
        afterChange?.(index, key, '0')
        return
      }

      if (!/^(\d+([.,]\d*)?|[.,]\d*)$/.test(v) && v !== '') return

      if (v.length > 1 && v[0] === '0' && v[1] !== '.' && v[1] !== ',') {
        v = v.replace(/^0+/, '')
      }

      setFieldValue(`productList[${index}].${key}`, v)
      afterChange?.(index, key, v)
    }
}
